// DOUBLE-CHARGE GUARD (owner go 19 Oct 2026), screen-only warning.
//
// Before Credit starts a new card request, the screen asks the server whether
// this event already has an approved card for the same amount in the last 20
// minutes that no checkout finished (any rep's cart, any iPad). If it does,
// this warning shows when, who and which card, and a new charge goes ahead
// only with a manager PIN. A normal sale never sees it, and a check that
// fails or is slow never holds a sale: it lets Credit go ahead (fail open).
// Nothing here touches the Clover payment or signature path.
import dayjs from 'dayjs';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useState } from 'react';

import { api } from 'api';
import { isMobile } from 'hooks';
import { ButtonColors } from 'types';
import { formatPrice } from 'utils/helpers';
import { parseAxiosError } from 'utils/error';
import { validateApprovalPassword } from 'utils/validation';
import { DuplicateCardCheck, DuplicateCardPayment } from 'api/payment/types';
import {
  Flex,
  Dialog,
  Button,
  DialogType,
  InputField,
  ButtonTypes,
  CommonStyles,
  ErrorMessage,
} from 'components';

// A little above the request's own timeout: covers a retry inside the API
// client too. Past it, Credit goes ahead.
export const DUPLICATE_CARD_GUARD_TIMEOUT_MS = 3_500;

/**
 * Asks the server. Returns the check only when it found a payment; null when
 * there is none OR the check failed or was too slow (fail open, logged).
 */
export const findDuplicateCard = async ({
  eventId,
  amountCents,
}: {
  eventId: number;
  amountCents: number;
}): Promise<DuplicateCardCheck | null> => {
  if (!eventId || !Number.isSafeInteger(amountCents) || amountCents <= 0) {
    return null;
  }
  const controller =
    typeof AbortController === 'undefined' ? undefined : new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const answer = await Promise.race([
      api.payment.checkDuplicateCard({
        eventId,
        amountCents,
        signal: controller?.signal,
      }),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          controller?.abort();
          reject(new Error('DUPLICATE_CARD_CHECK_TIMEOUT'));
        }, DUPLICATE_CARD_GUARD_TIMEOUT_MS);
      }),
    ]);
    const data = answer?.data;
    return data?.duplicate && data.payments?.length ? data : null;
  } catch (error) {
    console.warn(
      '[PAYMENT] Duplicate card check failed; Credit continues (fail open)',
      error
    );
    return null;
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
};

const describePayment = (payment: DuplicateCardPayment) =>
  [
    dayjs(payment.approvedAt).format('h:mm A'),
    payment.sameRep ? `${payment.repName} (you)` : payment.repName,
    payment.last4 ? `card ending ${payment.last4}` : null,
    payment.parked ? 'parked sale' : null,
  ]
    .filter(Boolean)
    .join(' · ');

const validationSchema = Yup.object().shape({
  approvalPassword: validateApprovalPassword,
});

interface DuplicateCardWarningProps {
  amount: number;
  leaderId?: number;
  onCancel: () => void;
  onApproved: () => void;
  check: DuplicateCardCheck | null;
}

// Nothing is rendered (and no hook runs) until a check found a payment.
export const DuplicateCardWarning = ({
  check,
  ...props
}: DuplicateCardWarningProps) =>
  check ? <WarningDialog {...props} check={check} /> : null;

const WarningDialog = ({
  check,
  amount,
  leaderId,
  onCancel,
  onApproved,
}: DuplicateCardWarningProps & { check: DuplicateCardCheck }) => {
  const [error, setError] = useState<string | null>(null);
  // Phones: the two buttons share the row edge to edge (tablet and up keep the
  // dialog's right-aligned 112 px buttons).
  const mobile = isMobile();

  const single = check.payments.length === 1;

  return (
    <Dialog
      open
      showCloseButton
      onClose={onCancel}
      width="min(600px, 90vw)"
      closeOnDocumentClick={false}
      title="Possible double charge"
    >
      {(_close, _text, ButtonWrap) => (
        <Formik
          validationSchema={validationSchema}
          initialValues={{ approvalPassword: '' }}
          onSubmit={async ({ approvalPassword }) => {
            setError(null);
            if (!leaderId) {
              setError(
                'This event has no manager PIN. Ask the event leader before charging again.'
              );
              return;
            }
            try {
              const { data } = await api.payment.verifyManagerPin({
                leaderId,
                approvalPassword,
              });
              if (!data?.isPasswordValid) {
                setError('Wrong manager PIN.');
                return;
              }
              console.info(
                '[PAYMENT] Manager PIN approved a card request despite an unfinished approved card',
                { payments: check.payments.map((payment) => payment.paymentId) }
              );
              onApproved();
            } catch (verifyError) {
              setError(parseAxiosError(verifyError) || 'Wrong manager PIN.');
            }
          }}
        >
          {({ dirty, isSubmitting }) => (
            <CommonStyles.Form gap={24} autoComplete="off">
              <Flex column gap={16}>
                <p style={{ margin: 0, lineHeight: 1.6 }}>
                  {single ? 'A card was' : 'Cards were'} already approved for{' '}
                  <span className="semibold">{formatPrice(amount)}</span> at
                  this event in the last {check.windowMinutes} minutes, with no
                  sale yet:
                </p>
                <ul
                  style={{
                    gap: 8,
                    margin: 0,
                    lineHeight: 1.6,
                    display: 'flex',
                    borderRadius: 12,
                    listStyle: 'none',
                    padding: '12px 16px',
                    background: '#fff6e5',
                    flexDirection: 'column',
                    border: '1px solid #f3c56b',
                  }}
                >
                  {check.payments.map((payment) => (
                    <li
                      className="semibold"
                      key={payment.paymentId}
                      style={{ overflowWrap: 'anywhere' }}
                    >
                      {describePayment(payment)}
                    </li>
                  ))}
                </ul>
                <p style={{ margin: 0, lineHeight: 1.6 }}>
                  Ask the customer if they were already charged. Charge again
                  only for a new purchase, with a manager PIN.
                </p>
                <InputField
                  number
                  autofill="off"
                  label="Manager PIN"
                  name="approvalPassword"
                  placeholder="Manager PIN"
                  autoComplete="one-time-code"
                />
                {error && <ErrorMessage>{error}</ErrorMessage>}
              </Flex>

              <ButtonWrap type={DialogType.Plain}>
                <Button
                  onClick={onCancel}
                  fullWidth={mobile}
                  text="Don't charge"
                  colorType={ButtonColors.LightBlue}
                />
                <Button
                  fullWidth={mobile}
                  text="Charge again"
                  type={ButtonTypes.Submit}
                  colorType={ButtonColors.LightRed}
                  disabled={!dirty || isSubmitting}
                />
              </ButtonWrap>
            </CommonStyles.Form>
          )}
        </Formik>
      )}
    </Dialog>
  );
};
